import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Power, PackageOpen, IndianRupee } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, PageHeader } from "@/components/Ui";

/**
 * Top-up Pack catalogue admin (super_admin only).
 *
 * These are the one-time SKUs that the UpgradePromptModal surfaces when a
 * tenant hits a monthly quota. Admins can create / edit / deactivate
 * packs here; pricing changes apply to the *next* purchase (existing
 * pending Razorpay orders carry the price at creation time).
 *
 * Why not hard-delete? Each pack code may have a long historical sales
 * trail in `tenant_topups` — deleting the catalogue row would orphan
 * the historical pack_code reference. We soft-delete (is_active=false)
 * for packs with sales; brand-new packs with zero sales can be hard-deleted.
 */

const RESOURCE_OPTIONS = [
    { value: "whatsapp_msgs_per_month", label: "WhatsApp messages / month" },
    { value: "ai_credits_per_month",    label: "AI credits / month" },
    { value: "cnr_sync_per_month",      label: "eCourts CNR lookups / month" },
];

const blankForm = {
    code: "", name: "", description: "",
    resource: "whatsapp_msgs_per_month",
    units: 500, price_inr: 499,
    category: "", display_order: 100, is_active: true,
};

export default function AdminTopupPacks() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);  // null for "create", code string for "edit"
    const [form, setForm] = useState(blankForm);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/topup-packs?include_inactive=true");
            setItems(data.packs || []);
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...blankForm });
        setOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p.code);
        setForm({
            code: p.code, name: p.name, description: p.description || "",
            resource: p.resource, units: p.units, price_inr: p.price_inr,
            category: p.category || "", display_order: p.display_order || 100,
            is_active: p.is_active !== false,
        });
        setOpen(true);
    };

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            if (editing) {
                // PATCH only the editable fields (resource is intentionally read-only)
                const body = {
                    name: form.name,
                    description: form.description,
                    units: Number(form.units),
                    price_inr: Number(form.price_inr),
                    category: form.category,
                    display_order: Number(form.display_order),
                    is_active: form.is_active,
                };
                await api.patch(`/admin/topup-packs/${editing}`, body);
                toast.success(`Pack ${editing} updated`);
            } else {
                await api.post("/admin/topup-packs", {
                    ...form,
                    units: Number(form.units),
                    price_inr: Number(form.price_inr),
                    display_order: Number(form.display_order),
                });
                toast.success(`Pack ${form.code} created`);
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(apiError(e));
        } finally { setSaving(false); }
    };

    const toggleActive = async (p) => {
        try {
            await api.patch(`/admin/topup-packs/${p.code}`, { is_active: !p.is_active });
            toast.success(`Pack ${p.code} ${p.is_active ? "deactivated" : "reactivated"}`);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const removePack = async (p) => {
        if (!window.confirm(`Delete pack '${p.code}'?\n\nIf the pack has historical sales it will be deactivated (soft-delete) so existing tenant_topups records keep their reference. Brand-new packs with zero sales will be hard-deleted.`)) return;
        try {
            const { data } = await api.delete(`/admin/topup-packs/${p.code}`);
            toast.success(data.soft_deleted
                ? `Deactivated (${data.sold_count} historical sales preserved)`
                : `Hard-deleted ${p.code}`);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const grouped = useMemo(() => {
        const out = {};
        items.forEach(p => {
            const r = p.resource || "other";
            (out[r] = out[r] || []).push(p);
        });
        return out;
    }, [items]);

    const resourceLabel = (r) => RESOURCE_OPTIONS.find(o => o.value === r)?.label || r;

    return (
        <div className="space-y-6" data-testid="admin-topup-packs">
            <PageHeader
                title="Top-up Pack Catalogue"
                subtitle="One-time transactional packs offered to tenants when they hit a monthly quota. Prices apply to the next purchase; existing pending orders keep their original amount."
                action={
                    <GoldButton onClick={openCreate} testId="admin-topup-create-btn">
                        <Plus className="w-4 h-4 mr-1.5"/> New pack
                    </GoldButton>
                }
            />

            {loading && <div className="text-sm text-muted-foreground" data-testid="admin-topup-loading">Loading packs…</div>}

            {!loading && items.length === 0 && (
                <div className="border border-dashed border-border p-12 text-center text-muted-foreground text-sm">
                    <PackageOpen className="w-8 h-8 mx-auto mb-2 opacity-50"/>
                    No packs yet. Create your first SKU.
                </div>
            )}

            {!loading && Object.entries(grouped).map(([resource, packs]) => (
                <section key={resource} data-testid={`admin-topup-group-${resource}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                        {resourceLabel(resource)} · {packs.length} {packs.length === 1 ? "pack" : "packs"}
                    </div>
                    <div className="border border-border divide-y divide-border">
                        {packs.map(p => (
                            <div key={p.code} className={`grid grid-cols-12 gap-3 px-4 py-3 items-center ${p.is_active ? "" : "opacity-50"}`} data-testid={`admin-topup-row-${p.code}`}>
                                <div className="col-span-12 sm:col-span-4">
                                    <div className="font-semibold text-sm flex items-center gap-2">
                                        {p.name}
                                        {!p.is_active && <span className="text-[9px] uppercase tracking-wider bg-muted text-muted-foreground px-1.5 py-0.5">Inactive</span>}
                                    </div>
                                    <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{p.code}</div>
                                </div>
                                <div className="col-span-4 sm:col-span-2 text-sm">
                                    <span className="font-mono">{p.units.toLocaleString()}</span>
                                    <div className="text-[10px] text-muted-foreground">units</div>
                                </div>
                                <div className="col-span-4 sm:col-span-2 text-sm">
                                    <span className="font-mono inline-flex items-center"><IndianRupee className="w-3 h-3"/>{Math.round(p.price_inr).toLocaleString()}</span>
                                    <div className="text-[10px] text-muted-foreground">₹{(p.price_inr / Math.max(1, p.units)).toFixed(2)}/unit</div>
                                </div>
                                <div className="col-span-4 sm:col-span-1 text-[11px] text-muted-foreground">#{p.display_order}</div>
                                <div className="col-span-12 sm:col-span-3 flex gap-2 justify-end">
                                    <button onClick={() => toggleActive(p)} title={p.is_active ? "Deactivate" : "Reactivate"}
                                        data-testid={`admin-topup-toggle-${p.code}`}
                                        className="p-1.5 border border-border hover:border-primary hover:text-primary transition">
                                        <Power className="w-3.5 h-3.5"/>
                                    </button>
                                    <button onClick={() => openEdit(p)} title="Edit"
                                        data-testid={`admin-topup-edit-${p.code}`}
                                        className="p-1.5 border border-border hover:border-primary hover:text-primary transition">
                                        <Pencil className="w-3.5 h-3.5"/>
                                    </button>
                                    <button onClick={() => removePack(p)} title="Delete"
                                        data-testid={`admin-topup-delete-${p.code}`}
                                        className="p-1.5 border border-border hover:border-rose-500 hover:text-rose-500 transition">
                                        <Trash2 className="w-3.5 h-3.5"/>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            ))}

            {/* Create / Edit modal */}
            <Modal open={open} onClose={() => setOpen(false)}
                title={editing ? `Edit pack · ${editing}` : "New top-up pack"}
                size="lg">
                <form onSubmit={save} className="space-y-4">
                    {!editing && (
                        <Field label="Code" hint="Lowercase letters, digits, underscores — used in URLs and webhook notes. Cannot be changed later.">
                            <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                                data-testid="admin-topup-form-code" required
                                placeholder="whatsapp_pack_500"
                                pattern="[a-z0-9_]+"/>
                        </Field>
                    )}
                    <Field label="Display name">
                        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                            data-testid="admin-topup-form-name" required placeholder="+500 WhatsApp messages"/>
                    </Field>
                    <Field label="Description" hint="One short line shown to the tenant inside the modal.">
                        <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                            data-testid="admin-topup-form-description" rows={2}
                            placeholder="One-time top-up — usable this month. No auto-renew."/>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Resource" hint="The monthly quota this pack boosts.">
                            <Select value={form.resource}
                                onChange={e => setForm({ ...form, resource: e.target.value })}
                                options={RESOURCE_OPTIONS}
                                disabled={!!editing}
                                data-testid="admin-topup-form-resource"/>
                            {editing && <div className="text-[10px] text-muted-foreground mt-1">Resource is locked after creation to preserve sales-history lineage.</div>}
                        </Field>
                        <Field label="Category" hint="Free-text grouping label (e.g. 'Communications', 'AI'). Optional.">
                            <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                                data-testid="admin-topup-form-category" placeholder="Communications"/>
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        <Field label="Units">
                            <Input type="number" min="1" value={form.units}
                                onChange={e => setForm({ ...form, units: e.target.value })}
                                data-testid="admin-topup-form-units" required/>
                        </Field>
                        <Field label="Price (₹)">
                            <Input type="number" min="1" step="0.01" value={form.price_inr}
                                onChange={e => setForm({ ...form, price_inr: e.target.value })}
                                data-testid="admin-topup-form-price" required/>
                        </Field>
                        <Field label="Display order" hint="Lower numbers appear first.">
                            <Input type="number" min="0" value={form.display_order}
                                onChange={e => setForm({ ...form, display_order: e.target.value })}
                                data-testid="admin-topup-form-order"/>
                        </Field>
                    </div>
                    <Field>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={form.is_active}
                                onChange={e => setForm({ ...form, is_active: e.target.checked })}
                                data-testid="admin-topup-form-active"
                                className="w-4 h-4 accent-primary"/>
                            <span className="text-sm">Active <span className="text-[10px] text-muted-foreground">(visible to tenants in the upgrade modal)</span></span>
                        </label>
                    </Field>
                    <div className="flex justify-end gap-2 pt-2 border-t border-border">
                        <button type="button" onClick={() => setOpen(false)}
                            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                            data-testid="admin-topup-form-cancel">Cancel</button>
                        <GoldButton type="submit" disabled={saving} data-testid="admin-topup-form-save">
                            {saving ? "Saving…" : (editing ? "Save changes" : "Create pack")}
                        </GoldButton>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
